// src/components/dashboard/candidate/profile/journey-steps/LocationStep.tsx
import React, { useState, useEffect } from 'react';
import { MapPin } from 'lucide-react';
import { CandidateProfileCreatePayload } from '@/lib/models/candidateProfile';
import { CandidateProfileService } from '@/services/CandidateProfileService';
import TagInput from '@/components/custom-ui/TagInput';

interface LocationStepProps {
    formData: Partial<CandidateProfileCreatePayload>;
    updateFormData: (updates: Partial<CandidateProfileCreatePayload>) => void;
}

export const LocationStep: React.FC<LocationStepProps> = ({ formData, updateFormData }) => {
    const [locations, setLocations] = useState<string[]>([]);
    const [isLoadingLocations, setIsLoadingLocations] = useState(false);

    // Load locations on mount
    useEffect(() => {
        loadLocations();
    }, []);

    const loadLocations = async () => {
        setIsLoadingLocations(true);
        try {
            const results = await CandidateProfileService.searchLocations('');
            setLocations(results.map(l => l.name));
        } catch (error) {
            console.error('Error loading locations:', error);
        } finally {
            setIsLoadingLocations(false);
        }
    };

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-500">
            {/* Header */}
            <div className="text-center mb-8">
                <div className="w-16 h-16 mx-auto mb-4 bg-[#A4C3B2] rounded-2xl flex items-center justify-center">
                    <MapPin className="w-8 h-8 text-[#1A4D4F]" />
                </div>
                <h2 className="text-2xl font-bold mb-2">Var vill du arbeta?</h2>
                <p className="text-gray-600">
                    Vi visar dig möjligheter nära dig.<br />
                    Du kan alltid ändra detta senare!
                </p>
            </div>

            {/* Selected location display */}
            {formData.location && (
                <div className="text-center p-6 bg-[#A4C3B2]/30 rounded-2xl border-2 border-[#A4C3B2]">
                    <div className="text-sm text-[#1A4D4F] font-medium mb-2">Din plats</div>
                    <div className="text-2xl font-bold text-[#212121] flex items-center justify-center gap-2">
                        <MapPin className="w-6 h-6" />
                        {formData.location}
                    </div>
                </div>
            )}

            {/* Location input */}
            <div>
                <label className="block text-sm font-semibold mb-2">
                    Stad eller kommun *
                </label>
                <TagInput
                    value={formData.location ? [formData.location] : []}
                    onChange={(values) => updateFormData({ location: values[0] || '' })}
                    suggestions={locations}
                    placeholder="Välj eller skriv din plats..."
                    maxItems={1}
                    suggestionsLabel="Populära platser"
                    createNewLabel="Lägg till"
                    createNewPrefix="Välj"
                />
            </div>
        </div>
    );
};